import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconBuildingSkyscraper, IconClock, IconFilePencil, IconInfoCircle } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Banner } from "../../../../components/common/banner";
import { Input } from "../../../../components/common/input";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { AddressFields } from "../../../../components/forms/address-fields";
import { SectionCard, Field } from "./section-card";
import { ContactChangeDialog, type ContactKind } from "./contact-change-dialog";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useMe, useMePartner } from "../../../../swr";
import { ChangeRequestType, type ChangeRequestDto } from "../../../../api";
import { useSubmitChangeRequest } from "../../hooks/useSubmitChangeRequest";
import {
  useMyDraftProfileChangeRequest,
  useMyPendingProfileChangeRequest,
} from "../../../change-requests/hooks/useMyPendingProfileChangeRequest";
import {
  mergeWithDraftChanges,
  withDraftValues,
} from "../../../change-requests/data/profile-draft";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { formatDate } from "../../../../utils/date";
import { useCountries } from "../../../products/hooks/useCountries";
import { useStates } from "../../../products/hooks/useStates";
import {
  MAX_LENGTH,
  validateBusinessName,
  validateCity,
  validateCrNumber,
  validateFullName,
  validatePostalCode,
  validateStreet,
  validateStreet2,
  validateTaxNumber,
} from "../../../../utils/validation";

type PartnerFallback = {
  name?: string | null;
  companyRegistry?: string | null;
  vat?: string | null;
  street?: string | null;
  street2?: string | null;
  city?: string | null;
  zip?: string | null;
  state?: { id?: number; name?: string | null } | null;
  country?: { id?: number; name?: string | null } | null;
} | null | undefined;

type ProfileForm = {
  companyName: string;
  crNumber: string;
  taxNumber: string;
  street: string;
  street2: string;
  city: string;
  zip: string;
  countryId: string;
  stateId: string;
  contactName: string;
};

type ProfileErrors = Partial<Record<keyof ProfileForm, string>>;

const PROFILE_VALIDATORS: Partial<Record<keyof ProfileForm, (value: string) => string | null>> = {
  companyName: validateBusinessName,
  crNumber: validateCrNumber,
  taxNumber: validateTaxNumber,
  street: validateStreet,
  street2: validateStreet2,
  city: validateCity,
  zip: validatePostalCode,
  countryId: (value) => (value ? null : "Please select a country."),
  contactName: validateFullName,
};

/** "+97455512345" → "+974 5551 2345"; anything unparseable is shown as stored. */
function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  return parsePhoneNumberFromString(phone)?.formatInternational() ?? phone;
}

/**
 * The account's email and phone number. They are sign-in identifiers, so
 * they aren't part of the reviewed profile form: each changes on its own,
 * once a code sent to the new value is confirmed.
 */
function AccountContactFields() {
  const { user } = useMe();
  const [changing, setChanging] = useState<ContactKind | null>(null);
  const changeButton = (kind: ContactKind, label: string) => (
    <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setChanging(kind)}>
      {label}
    </Button>
  );

  return (
    <>
      <Field
        label="Email Address"
        value={user?.email}
        action={changeButton("email", user?.email ? "Change" : "Add")}
      />
      <Field
        label="Phone number"
        value={formatPhone(user?.phoneNumber)}
        action={changeButton("phone", user?.phoneNumber ? "Change" : "Add")}
      />
      <ContactChangeDialog kind={changing} onClose={() => setChanging(null)} />
    </>
  );
}

export function CompanyProfileEmpty({ onStart }: { onStart: () => void }) {
  return (
    <>
      <div className="flex flex-col items-center gap-5 py-20">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-bgLight">
          <IconBuildingSkyscraper size={30} className="text-textMuted" />
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          <Heading variant="h2" tone="dark">Complete Your Company Profile</Heading>
          <Text variant="body" tone="muted" className="max-w-sm">
            Help us serve you better by providing your company information. This will streamline your ordering and delivery process.
          </Text>
        </div>
        <Button variant="primary" leftIcon={<IconBuildingSkyscraper size={16} />} onClick={onStart}>
          Get Started
        </Button>
      </div>

      <div className="flex items-center gap-4 rounded-2xl bg-accentBlueBanner px-6 py-4 text-accentBlue max-md:items-start max-md:px-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accentBlueTint">
          <IconInfoCircle size={22} />
        </div>
        <div className="flex flex-col gap-0.5">
          <Text variant="bodyBold" tone="inherit">Why do we need this information?</Text>
          <Text variant="body" tone="inherit">
            Company details help us verify your account, provide accurate invoicing, and ensure smooth delivery coordination.
          </Text>
        </div>
      </div>
    </>
  );
}

export function CompanyProfileCompletionBanner({
  onEdit,
}: {
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5 max-md:flex-col max-md:items-stretch max-md:px-4">
      <div className="flex items-center gap-4 max-md:items-start">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accentBlueBanner">
          <IconBuildingSkyscraper size={22} className="text-accentBlue" />
        </div>
        <div className="flex flex-col gap-0.5">
          <Heading variant="h3" tone="dark">
            Complete Your Company Profile
          </Heading>
          <Text variant="bodySm" tone="dark">
            Fill in the missing business details once, then keep everything visible on this page.
          </Text>
        </div>
      </div>
      <Button variant="primary" size="sm" onClick={onEdit}>
        Complete profile
      </Button>
    </div>
  );
}

export function CompanyProfileView() {
  const navigate = useNavigate();
  const { user } = useMe();
  const { partner, isSeller } = useMePartner();
  const fallback = partner as PartnerFallback;
  const historyHref = isSeller
    ? AppRoutes.CHANGE_REQUESTS
    : `${AppRoutes.ACCOUNT}?tab=change-requests`;
  const { data: pendingProfile } = useMyPendingProfileChangeRequest();
  const { data: countries } = useCountries();
  const { data: states } = useStates();

  // Build a map of `field key → human-readable proposed value` for inline
  // annotations. IDs (countryId/stateId) are resolved to display names so
  // users see "Pending: Qatar" not "Pending: 168".
  const pendingByKey: Record<string, string> = {};
  if (pendingProfile?.proposedChanges) {
    const proposed = pendingProfile.proposedChanges as Record<string, unknown>;
    for (const [key, raw] of Object.entries(proposed)) {
      if (raw === null || raw === undefined || raw === "") {
        pendingByKey[key] = "(empty)";
        continue;
      }
      if (key === "countryId") {
        const id = Number(raw);
        const match = countries?.find((c) => c.id === id);
        pendingByKey[key] = match?.name ?? String(raw);
      } else if (key === "stateId") {
        const id = Number(raw);
        const match = states?.find((s) => s.id === id);
        pendingByKey[key] = match?.name ?? String(raw);
      } else {
        pendingByKey[key] = String(raw);
      }
    }
  }

  const pendingCount = Object.keys(pendingByKey).length;
  const submittedAtLabel = pendingProfile?.submittedAt
    ? new Date(pendingProfile.submittedAt).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <div className="flex flex-col gap-4">
      {pendingProfile && (
        <div className="flex items-start gap-3 rounded-2xl border border-accentAmber bg-accentYellowTint px-4 py-3">
          <IconClock size={18} className="mt-0.5 shrink-0 text-accentAmber" />
          <div className="flex flex-1 flex-col gap-0.5">
            <Text variant="bodySmBold" tone="amber">
              {pendingCount} change{pendingCount === 1 ? "" : "s"} waiting for admin approval
            </Text>
            <Text variant="bodyXs" tone="amber">
              {submittedAtLabel ? `Submitted ${submittedAtLabel}. ` : ""}
              The values below still show what's currently live on your profile; pending values are noted under each field.
            </Text>
          </div>
          <button
            type="button"
            onClick={() => navigate(historyHref)}
            className="shrink-0 self-start"
          >
            <Text variant="bodySmBold" tone="amber" inline className="underline">
              View history
            </Text>
          </button>
        </div>
      )}

      <SectionCard title="Company Information">
        <Field
          label="Company Name"
          value={user?.companyName ?? fallback?.name ?? "—"}
          pending={pendingByKey.companyName}
        />
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Field
            label="Commercial Registration No."
            value={user?.crNumber ?? fallback?.companyRegistry ?? "—"}
            pending={pendingByKey.crNumber}
          />
          <Field
            label="VAT Registration Number"
            value={user?.taxNumber ?? fallback?.vat ?? "—"}
            pending={pendingByKey.taxNumber}
          />
        </div>
      </SectionCard>

      <SectionCard title="Company Address">
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Field
            label="Street address"
            value={user?.street ?? fallback?.street ?? "—"}
            pending={pendingByKey.street}
          />
          <Field
            label="Street address line 2"
            value={user?.street2 ?? fallback?.street2 ?? "—"}
            pending={pendingByKey.street2}
          />
          <Field
            label="City"
            value={user?.city ?? fallback?.city ?? "—"}
            pending={pendingByKey.city}
          />
          <Field
            label="ZIP / postal code"
            value={user?.zip ?? fallback?.zip ?? "—"}
            pending={pendingByKey.zip}
          />
          <Field
            label="State / province"
            value={user?.state?.name ?? fallback?.state?.name ?? "—"}
            pending={pendingByKey.stateId}
          />
          <Field
            label="Country"
            value={user?.country?.name ?? fallback?.country?.name ?? "—"}
            pending={pendingByKey.countryId}
          />
        </div>
      </SectionCard>

      <SectionCard title="Primary Contact">
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Field
            label="Full Name"
            value={user?.contactName ?? user?.name ?? "—"}
            pending={pendingByKey.contactName}
          />
          <AccountContactFields />
        </div>
      </SectionCard>
    </div>
  );
}

type CompanyProfileEditProps = {
  onCancel?: () => void;
  onSubmitted?: () => void;
  type?: ChangeRequestType;
};

/**
 * Waits for the profile and any saved draft before showing the form: the form
 * is seeded from them once, so a draft arriving later can't wipe what the
 * user already typed, and Submit can't create a second request while the
 * draft it should update is still loading.
 */
export function CompanyProfileEdit(props: CompanyProfileEditProps) {
  const { isLoading: isLoadingProfile } = useMePartner();
  const { data: draft, error: draftError } = useMyDraftProfileChangeRequest();
  const isLoadingDraft = draft === undefined && !draftError;

  if (isLoadingProfile || isLoadingDraft) {
    return <ShimmerLoader message="Loading your profile and any saved draft…" />;
  }

  return <CompanyProfileEditForm key={draft?.id ?? "new"} {...props} draft={draft ?? null} />;
}

function CompanyProfileEditForm({
  onCancel,
  onSubmitted,
  type = ChangeRequestType.BuyerProfileUpdate,
  draft,
}: CompanyProfileEditProps & { draft: ChangeRequestDto | null }) {
  const { user, mutate } = useMe();
  const { partner } = useMePartner();
  const fallback = partner as PartnerFallback;
  const { mutate: mutatePendingProfile } = useMyPendingProfileChangeRequest();
  const { mutate: mutateDraft } = useMyDraftProfileChangeRequest();
  const { submit, isLoading } = useSubmitChangeRequest();

  const initial = useMemo(
    () => ({
      companyName: user?.companyName ?? fallback?.name ?? "",
      crNumber: user?.crNumber ?? fallback?.companyRegistry ?? "",
      taxNumber: user?.taxNumber ?? fallback?.vat ?? "",
      street: user?.street ?? fallback?.street ?? "",
      street2: user?.street2 ?? fallback?.street2 ?? "",
      city: user?.city ?? fallback?.city ?? "",
      zip: user?.zip ?? fallback?.zip ?? "",
      countryId:
        user?.country?.id?.toString() ?? fallback?.country?.id?.toString() ?? "",
      stateId: user?.state?.id?.toString() ?? fallback?.state?.id?.toString() ?? "",
      contactName: user?.contactName ?? user?.name ?? "",
    }),
    [
      user?.companyName,
      user?.crNumber,
      user?.taxNumber,
      user?.street,
      user?.street2,
      user?.city,
      user?.zip,
      user?.country?.id,
      user?.state?.id,
      user?.contactName,
      user?.name,
      fallback?.name,
      fallback?.companyRegistry,
      fallback?.vat,
      fallback?.street,
      fallback?.street2,
      fallback?.city,
      fallback?.zip,
      fallback?.country?.id,
      fallback?.state?.id,
    ],
  );

  // `initial` (the live profile) stays the diff baseline; a saved draft only
  // pre-fills the form. Seeded once — `CompanyProfileEdit` mounts this after
  // the profile and draft have loaded.
  const [form, setForm] = useState<ProfileForm>(() => withDraftValues(initial, draft));
  const [errors, setErrors] = useState<ProfileErrors>({});

  /** Applies a partial edit and clears the errors of the fields it touched. */
  const update = (patch: Partial<ProfileForm>) => {
    setForm((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      for (const key of Object.keys(patch) as (keyof ProfileForm)[]) {
        if (patch[key] !== form[key]) delete next[key];
      }
      return next;
    });
  };

  const diff = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    (Object.keys(form) as (keyof typeof form)[]).forEach((k) => {
      if (form[k] !== initial[k]) {
        if (k === "street") {
          out.street = form[k].trim();
        } else if (k === "countryId") {
          out[k] = form[k] ? Number.parseInt(form[k], 10) : null;
        } else if (k === "stateId") {
          out[k] = form[k] ? Number.parseInt(form[k], 10) : null;
        } else {
          out[k] = form[k].trim();
        }
      }
    });
    return out;
  };

  const handleSubmit = async () => {
    // Only the changed fields are submitted, so only those are checked.
    const nextErrors: ProfileErrors = {};
    for (const key of Object.keys(form) as (keyof ProfileForm)[]) {
      if (form[key] === initial[key]) continue;
      const error = PROFILE_VALIDATORS[key]?.(form[key]);
      if (error) nextErrors[key] = error;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    // Continuing a draft: keep the changes it holds for fields this form
    // doesn't edit (the backend replaces the draft's changes on update), and
    // let a draft that only changes such fields still be submitted.
    const proposed = mergeWithDraftChanges(draft, Object.keys(form), diff());
    if (Object.keys(proposed).length === 0) return;
    try {
      await submit({
        type,
        proposedChanges: proposed,
        draftId: draft?.id,
      });
      await Promise.all([mutate(), mutatePendingProfile(), mutateDraft()]);
      onSubmitted?.();
    } catch {
      // toast handled in hook
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {draft && (
        <Banner tone="warning" icon={<IconFilePencil size={18} />}>
          You're continuing your draft saved {formatDate(draft.updatedAt, true)}.
          Submitting sends it for review.
        </Banner>
      )}
      <SectionCard title="Company Information">
        <Input
          label="Company Name"
          value={form.companyName}
          maxLength={MAX_LENGTH.businessName}
          error={errors.companyName}
          onChange={(e) => update({ companyName: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Input
            label="Commercial Registration No."
            value={form.crNumber}
            maxLength={MAX_LENGTH.registrationNumber}
            error={errors.crNumber}
            onChange={(e) => update({ crNumber: e.target.value })}
          />
          <Input
            label="VAT Registration Number"
            value={form.taxNumber}
            maxLength={MAX_LENGTH.registrationNumber}
            error={errors.taxNumber}
            onChange={(e) => update({ taxNumber: e.target.value })}
          />
        </div>
      </SectionCard>

      <SectionCard title="Company Address">
        <AddressFields
          value={{
            street: form.street,
            street2: form.street2,
            city: form.city,
            zip: form.zip,
            countryId: form.countryId,
            stateId: form.stateId,
          }}
          errors={errors}
          onChange={(next) =>
            update({
              street: next.street,
              street2: next.street2,
              city: next.city,
              zip: next.zip,
              countryId: next.countryId,
              stateId: next.stateId,
            })
          }
        />
      </SectionCard>

      <SectionCard title="Primary Contact">
        <div className="grid grid-cols-2 gap-4 max-md:grid-cols-1">
          <Input
            label="Full Name"
            value={form.contactName}
            maxLength={MAX_LENGTH.personName}
            error={errors.contactName}
            onChange={(e) => update({ contactName: e.target.value })}
          />
          <AccountContactFields />
        </div>
      </SectionCard>

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? "Submitting…" : "Submit for review"}
        </Button>
      </div>
    </div>
  );
}
